import { Injectable } from '@angular/core';
import { defer, from, map, Observable, firstValueFrom } from 'rxjs';
import { ReportsConvertedApiBaseService } from './reports-converted-api-base.service';
import { normalizeReportsConvertedPath } from './reports-converted-path.util';

const OVERRIDE_LIST_PATHS = new Set(['', 'agencies', 'products', 'carriers', 'report-configs']);

@Injectable({ providedIn: 'root' })
export class OverrideReportsConvertedApiService extends ReportsConvertedApiBaseService {
  protected readonly basePath = '/api/override';

  load<T>(path = '', query?: unknown): Observable<T> {
    const normalizedPath = normalizeReportsConvertedPath(path);
    const listEndpointPath = normalizedPath ? `${normalizedPath}/get-list` : 'get-list';

    if (OVERRIDE_LIST_PATHS.has(normalizedPath)) {
      return defer(() => from(super.post<unknown>(listEndpointPath, query ?? {}))).pipe(
        map(response => this.mapLegacyListResponse<T>(normalizedPath, response)),
      );
    }

    return defer(() => from(super.get<T>(normalizedPath, query)));
  }

  loadPromise<T>(path = '', query?: unknown): Promise<T> {
    return firstValueFrom(this.load<T>(path, query));
  }

  create<T>(path = '', body?: unknown): Promise<T> {
    return super.post<T>(normalizeReportsConvertedPath(path), body);
  }

  update<T>(path = '', body?: unknown): Promise<T> {
    return super.put<T>(normalizeReportsConvertedPath(path), body);
  }

  delete<T>(path = ''): Promise<T> {
    return super.delete<T>(normalizeReportsConvertedPath(path));
  }

  loadReportConfigPdf(
    reportConfigId: number,
    payload: {
      fields?: Array<{ key: string; value: unknown }>;
    } = {},
  ): Promise<Blob> {
    return this.request<Blob>(`report-configs/${reportConfigId}/pdf`, {
      method: 'POST',
      body: {
        fields: payload.fields ?? [],
      },
      responseType: 'blob',
    });
  }
  private mapLegacyListResponse<T>(path: string, response: unknown): T {
    if (!this.isRecord(response)) {
      return response as T;
    }

    const keyMap: Record<string, string> = {
      '': 'overrides',
      agencies: 'mappings',
      products: 'options',
      carriers: 'options',
      'report-configs': 'configs',
    };

    const mappedKey = keyMap[path];
    if (!mappedKey || mappedKey in response) {
      return response as T;
    }

    const data = response.data;
    if (!Array.isArray(data)) {
      return response as T;
    }

    return {
      ...response,
      [mappedKey]: data,
    } as T;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
}
